import { useQuery } from '@apollo/client';
import { Button, Card, CardContent, IconButton, Tooltip, Typography } from '@material-ui/core';
import { Send } from '@material-ui/icons';
import { Skeleton } from '@material-ui/lab';
import * as React from 'react';
import { useLocation } from 'react-router-dom';
import { v4 } from 'uuid';

import { Editor } from 'react-text-editor';

import { interpolate } from 'shared/models/sequence';
import { EmailAddress } from 'shared/types/email-compose';

import { composeEmail, replyToEmail } from '../../email-compose/actions';
import {
    AGENT_EMAIL_COMPOSER_EMAIL_HISTORY,
    AGENT_EMAIL_COMPOSER_RECRUITER_DATA,
    AGENT_EMAIL_COMPOSER_USER_DATA,
    AgentDraftEmail,
    AgentEmailComposerEmailHistoryData,
    AgentEmailComposerRecruiterData,
    EmailAccount,
    GET_AGENT_DRAFT_EMAIL
} from '../../graphql/queries/agent';
import { useSlides } from '../../hooks/use-candidate-slides';
import { useReduxDispatch, useReduxState } from '../../hooks/use-redux';
import type { Communication } from '../../state';
import { EmailData } from './agent-types';

interface ComposerData {
    account: EmailAddress;
    accountOptions: EmailAddress[];
    toRecipients: EmailAddress[];
    threadId: string | null;
    threadSubject: string | null;
}

function isOnCandidatePage(pathname: string, personId: string, jobId: string): boolean {
    const expectedPath = `/job/${jobId}/board/candidate/${personId}`;
    return pathname === expectedPath;
}

function getCandidateComposerData(
    recruiterData: AgentEmailComposerRecruiterData,
    emailHistory: AgentEmailComposerEmailHistoryData | undefined
): ComposerData | null {
    const { recruiter, person } = recruiterData.candidate;

    if (!recruiter?.emailAccounts?.length) return null;

    const accountOptions = recruiter.emailAccounts.map((acc) => ({
        address: acc.email,
        name: acc.name
    }));

    let account: EmailAddress | undefined;
    let toRecipients: EmailAddress[] = [];
    let threadId: string | null = null;
    let threadSubject: string | null = null;

    // Find the most recent email from recruiter's accounts, prioritizing current job
    const lastRecruiterComm = emailHistory?.jobEmailMessages?.[0] || emailHistory?.allEmailMessages?.[0];

    if (lastRecruiterComm) {
        const matchingAccount = accountOptions.find((a) => a.address === lastRecruiterComm.account);
        if (matchingAccount) {
            account = matchingAccount;
        }

        // Extract recipient from headers
        const recruiterEmailSet = new Set(recruiter.emailAccounts.map((acc) => acc.email));
        if (lastRecruiterComm.from?.address && !recruiterEmailSet.has(lastRecruiterComm.from.address)) {
            toRecipients = [lastRecruiterComm.from];
        } else if (lastRecruiterComm.to?.length > 0) {
            toRecipients = lastRecruiterComm.to.filter((addr) => !recruiterEmailSet.has(addr.address));
        }

        // Use thread info only from job-specific communication
        const jobComm = emailHistory?.jobEmailMessages?.[0];
        if (jobComm) {
            threadId = jobComm.threadId;
            threadSubject = jobComm.subject;
        }
    }

    // Fallback: use first account if no prior communication
    if (!account && accountOptions.length > 0) {
        account = accountOptions[0];
    }

    // Fallback: use person's contacts if no recipient found from email history
    if (toRecipients.length === 0 && person.contacts?.length > 0) {
        toRecipients = [{ address: person.contacts[0].value, name: '' }];
    }

    if (!account) return null;

    return { account, accountOptions, toRecipients, threadId, threadSubject };
}

function getUserComposerData(emailAccounts: EmailAccount[]): ComposerData | null {
    if (!emailAccounts.length) return null;

    const userAcc = emailAccounts[0];
    const account = { address: userAcc.email, name: userAcc.name };

    return { account, accountOptions: [account], toRecipients: [], threadId: null, threadSubject: null };
}

interface EmailDraftFeedProps {
    draftEmailId: string;
}

const skeletonDimensions = { width: 120, height: 20 };
const DRAFT_SKELETON_SUBJECT_WIDTH = 240;
const DRAFT_SKELETON_SUBJECT_HEIGHT = 24;
const DRAFT_SKELETON_BODY_HEIGHT = 140;

export const EmailDraftFeed: React.FC<EmailDraftFeedProps> = ({ draftEmailId }) => {
    const { data: draftData, loading: draftLoading } = useQuery<{ draft: AgentDraftEmail | null }>(
        GET_AGENT_DRAFT_EMAIL,
        {
            variables: { id: draftEmailId }
        }
    );

    const dispatch = useReduxDispatch();
    const user = useReduxState((state) => state.session.user);
    const emailContentBlacklistedDomains = useReduxState(
        (state) => state.appConstants.constants.emailContentBlacklistedDomains
    );
    const location = useLocation();
    const { setList } = useSlides();

    const draft = draftData?.draft || null;
    const showDraftSkeleton = draftLoading || !draft;
    const email: EmailData | null = draft
        ? {
              body: draft.body,
              clientId: draft.clientId || undefined,
              jobId: draft.jobId || undefined,
              personId: draft.personId || undefined,
              recipientType: draft.recipientType,
              subject: draft.subject
          }
        : null;

    const sentAt = draft?.sentAt;
    const isSent = typeof sentAt === 'number' && sentAt > 0;
    const isCandidate = email?.recipientType === 'candidate';

    // Query 1: Get recruiter info (accounts, signature) and person contacts
    const { data: recruiterData, loading: recruiterLoading } = useQuery<AgentEmailComposerRecruiterData>(
        AGENT_EMAIL_COMPOSER_RECRUITER_DATA,
        {
            skip: !isCandidate || !email?.personId || !email?.jobId,
            variables: {
                jobId: email?.jobId || '',
                personId: email?.personId || ''
            }
        }
    );

    const recruiterEmails = recruiterData?.candidate?.recruiter?.emailAccounts?.map((acc) => acc.email) ?? [];

    // Query 2: Get email history from recruiter's accounts (only runs after we have the accounts)
    const { data: emailHistoryData, loading: emailHistoryLoading } = useQuery<AgentEmailComposerEmailHistoryData>(
        AGENT_EMAIL_COMPOSER_EMAIL_HISTORY,
        {
            skip: !isCandidate || !email?.personId || !email?.jobId || recruiterEmails.length === 0,
            variables: {
                jobId: email?.jobId || '',
                personId: email?.personId || '',
                recruiterEmails
            }
        }
    );

    // Client/Internal query - fetches user's personal email account
    const { data: userData, loading: userLoading } = useQuery<{ emailAccounts: EmailAccount[] }>(
        AGENT_EMAIL_COMPOSER_USER_DATA,
        {
            skip: showDraftSkeleton || isCandidate,
            variables: { userEmail: user.email }
        }
    );

    if (showDraftSkeleton) {
        return (
            <Card variant="outlined" className="agent__emailPreviewCard">
                <CardContent className="agent__emailPreviewCardContent">
                    <div className="agent__emailPreviewHeader">
                        <Typography variant="subtitle1" className="agent__emailPreviewSubject">
                            <Skeleton
                                variant="text"
                                width={DRAFT_SKELETON_SUBJECT_WIDTH}
                                height={DRAFT_SKELETON_SUBJECT_HEIGHT}
                            />
                        </Typography>
                    </div>
                    <Skeleton variant="rect" width="100%" height={DRAFT_SKELETON_BODY_HEIGHT} />
                </CardContent>
            </Card>
        );
    }

    if (!email) {
        return null;
    }

    const candidateLoading = recruiterLoading || (recruiterEmails.length > 0 && emailHistoryLoading);
    const loading = isCandidate ? candidateLoading : userLoading;
    const recruiterAccounts = recruiterData?.candidate?.recruiter?.emailAccounts;

    // Get the appropriate signature for preview and composer
    const signature = isCandidate
        ? (recruiterData?.candidate?.recruiter?.signature ?? recruiterData?.candidate?.recruiter?.name ?? '')
        : (user.signature ?? '');

    // Replace signature placeholder for both preview and composer
    const bodyWithSignature = loading ? email.body : interpolate(email.body, { senderSignature: signature });

    // Compute thread info for subject display and composer
    const threadInfo =
        isCandidate && recruiterData?.candidate ? getCandidateComposerData(recruiterData, emailHistoryData) : null;
    const displaySubject =
        threadInfo?.threadId && threadInfo?.threadSubject
            ? `Re: ${threadInfo.threadSubject.replace(/^Re:\s*/i, '')}`
            : email.subject;

    const handleOpenComposer = () => {
        if (loading || isSent) return;

        let composerData: ComposerData | null = null;

        if (isCandidate && recruiterData?.candidate) {
            composerData = getCandidateComposerData(recruiterData, emailHistoryData);

            // If not on the candidate page, open the candidate slide first
            if (composerData && !isOnCandidatePage(location.pathname, email.personId, email.jobId)) {
                const candidateKey = { personId: email.personId, jobId: email.jobId };
                setList([candidateKey], candidateKey);
            }
        } else if (userData?.emailAccounts) {
            composerData = getUserComposerData(userData.emailAccounts);
        }

        if (!composerData) return;

        const { account, accountOptions, toRecipients, threadId } = composerData;

        const initialBody = interpolate(email.body, { senderSignature: signature });

        const replyMessage = threadId ? emailHistoryData?.jobEmailMessages?.[0] : null;
        if (replyMessage) {
            const message = {
                ...replyMessage,
                headers: {
                    bcc: replyMessage.bcc || [],
                    cc: replyMessage.cc || [],
                    from: replyMessage.from,
                    to: replyMessage.to || []
                },
                subject: displaySubject
            } as unknown as Communication;

            dispatch(
                replyToEmail(
                    accountOptions,
                    emailContentBlacklistedDomains || '',
                    message,
                    true,
                    true,
                    {
                        canEditToRecipients: true,
                        draftEmailId,
                        jobId: email.jobId,
                        personId: email.personId
                    },
                    initialBody
                )
            );
            return;
        }

        dispatch(
            composeEmail({
                account,
                accountOptions,
                attachments: [],
                body: initialBody,
                canEditToRecipients: true,
                draftEmailId,
                draftSavedAt: null,
                headers: {
                    bcc: [],
                    cc: [],
                    subject: displaySubject,
                    to: toRecipients
                },
                jobId: email.jobId,
                personId: email.personId,
                threadId,
                windowId: v4()
            })
        );
    };

    const hasAccount = isCandidate
        ? recruiterAccounts && recruiterAccounts.length > 0
        : userData?.emailAccounts?.length;

    const openComposerButton =
        !loading && hasAccount ? (
            <div className="agent__emailSendButtonContainer">
                <Tooltip title={isSent ? 'Email has already been sent' : 'Open this draft in the email composer'}>
                    <span>
                        <Button
                            variant="outlined"
                            size="small"
                            color="primary"
                            disabled={isSent}
                            onClick={handleOpenComposer}
                            className="agent__emailPreviewButton"
                        >
                            Open in Composer
                        </Button>
                    </span>
                </Tooltip>
            </div>
        ) : null;

    // Show skeleton in place of signature while loading
    const editorContent = loading ? (
        <div className="agent__emailEditorSkeleton">
            <Editor
                value={email.body.replace(/\{\{Sender Signature\}\}/g, '')}
                disabled={true}
                className="agent__emailEditor"
            />
            <Skeleton variant="text" width={skeletonDimensions.width} height={skeletonDimensions.height} />
        </div>
    ) : (
        <Editor value={bodyWithSignature} disabled={true} className="agent__emailEditor" />
    );

    const iconButton =
        !loading && hasAccount ? (
            <Tooltip title={isSent ? 'Email has already been sent' : 'Open this draft in the email composer'}>
                <span>
                    <IconButton
                        disabled={isSent}
                        onClick={handleOpenComposer}
                        className="agent__emailPreviewIconButton"
                    >
                        <Send />
                    </IconButton>
                </span>
            </Tooltip>
        ) : null;

    return (
        <Card variant="outlined" className="agent__emailPreviewCard">
            <CardContent className="agent__emailPreviewCardContent">
                <div className="agent__emailPreviewHeader">
                    <Typography variant="subtitle1" className="agent__emailPreviewSubject">
                        {displaySubject}
                    </Typography>
                    {iconButton}
                </div>
                {editorContent}
                {openComposerButton}
            </CardContent>
        </Card>
    );
};
